/**
 * SystemStatusCard — Kanal-Gesundheit pro Firma + globale Checks (DB, KI, SMTP).
 */
import { useEffect, useState } from "react";
import axios from "axios";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Activity, Loader2, CheckCircle2, XCircle, AlertTriangle } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

function Chip({ ok, warn, label, hint }) {
  const cls = ok
    ? "bg-emerald-50 text-emerald-700 border-emerald-200"
    : warn
      ? "bg-amber-50 text-amber-700 border-amber-200"
      : "bg-gray-50 text-gray-400 border-gray-200";
  const Icon = ok ? CheckCircle2 : warn ? AlertTriangle : XCircle;
  return (
    <span className={`inline-flex items-center gap-1 border rounded-full px-2 py-0.5 text-[10px] ${cls}`} title={hint || ""}>
      <Icon className="w-3 h-3" /> {label}
    </span>
  );
}

export const SystemStatusCard = () => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    axios.get(`${API}/system/status`, { withCredentials: true })
      .then((r) => setData(r.data))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  if (loading) {
    return (
      <Card className="border-[var(--studio-border)] shadow-none" data-testid="system-status-card">
        <CardContent className="pt-4 pb-3 flex items-center gap-2 text-sm text-[var(--studio-text-muted)]">
          <Loader2 className="w-4 h-4 animate-spin" /> Prüfe System-Status…
        </CardContent>
      </Card>
    );
  }
  if (!data) return null;
  const g = data.global;

  return (
    <Card className="border-[var(--studio-border)] shadow-none" data-testid="system-status-card">
      <CardHeader className="pb-2">
        <CardTitle className="text-base flex flex-wrap items-center gap-2">
          <Activity className="w-4 h-4 text-[#2563EB]" /> System-Status
          <div className="flex-1" />
          <Chip ok={g.db_ok} label="Datenbank" />
          <Chip ok={g.ai_key} label="KI" />
          <Chip ok={g.smtp_configured} warn={!g.smtp_configured} label="E-Mail (SMTP)" hint={g.smtp_configured ? "" : "SMTP unter E-Mail-Einstellungen konfigurieren"} />
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-1.5">
        {data.companies.map((c) => (
          <div key={c.company_id} className="flex flex-wrap items-center gap-1.5 border-b border-[var(--studio-border)] last:border-0 py-1.5" data-testid={`system-status-${c.company_id}`}>
            <span className="text-xs font-medium w-40 truncate">{c.name}</span>
            <Chip ok={c.meta.connected && c.meta.token_ok !== false} warn={c.meta.connected && c.meta.token_ok === false} label="Meta" hint={c.meta.token_ok === false ? (c.meta.token_error || "Token ungültig — neu verbinden") : ""} />
            <Chip ok={c.page_assigned} label="Seite" hint="Facebook/Instagram-Seite zugewiesen" />
            <Chip ok={c.meta.ads_scope && c.meta.ad_account} warn={c.meta.connected && !c.meta.ads_scope} label="Ads" hint={!c.meta.ads_scope ? "Ads-Berechtigungen fehlen — Meta neu verbinden" : !c.meta.ad_account ? "Werbekonto zuweisen" : ""} />
            <Chip ok={c.tiktok} label="TikTok" />
            <Chip ok={c.linkedin} label="LinkedIn" />
            <Chip ok={c.google_ads} label="Google Ads" />
            <Chip ok={c.meta_pixel} label="Pixel" hint="Meta Pixel-ID auf der Leads-Seite" />
          </div>
        ))}
      </CardContent>
    </Card>
  );
};
